import React, { useEffect, useState } from 'react';
import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import Image from '../../components/images/Image';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo, formatDate, logout, teamPhotoOf } from '../../utils';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import NotFound from '../../components/common/NotFound';
import useResetSubmittingOnRestore from '../../hooks/useResetSubmittingOnRestore';

const Page = () => {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    fetchUserInfo('/team-select', setUserInfo, setLoading, setError);
  }, []);

  // Keep the team cards clickable after the browser restores this page from the
  // back/forward cache (otherwise the `submitting` lock stays stuck). See the hook.
  useResetSubmittingOnRestore(setSubmitting);

  const handleTeamSelect = async (teamId, eventId) => {
    if (submitting) return;
    try {
      setSubmitting(teamId);
      const response = await fetch('/api/team-select', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          credentials: 'include',
        },
        body: JSON.stringify({ team_id: teamId, event_id: eventId }),
      });
      if (!response.ok) throw new Error('Failed to select team');
      await response.json();
      sessionStorage.setItem('otherTeamId', teamId);
      window.location.href = '/home';
    } catch (err) {
      console.error('Error selecting team:', err);
      setSubmitting(null);
    }
  };

  const teams = userInfo?.teams || [];

  return (
    <Screen>
      <div className="top">
        <div className="event-select-shell">
          <div className="event-select-header">
            <Back to="/role" />
            <h2 className="event-select-header__title">{t('selectYourTeam')}</h2>
            <p className="event-select-header__sub">{t('selectYourTeamSub')}</p>
            <button
              className="event-select-header__logout"
              onClick={logout}
              aria-label={t('logout')}
            >
              <Icon name="sign-out-alt" theme="primary" />
            </button>
          </div>

          {teams.length > 0 ? (
            <div className="event-list">
              {teams.map((team) => {
                const isSubmitting = submitting === team.team_id;
                return (
                  <Card
                    key={team.id}
                    mx="0"
                    mb="2"
                    className={`event-card ${isSubmitting ? 'event-card--loading' : ''}`}
                    onClick={() => handleTeamSelect(team.team_id, team.event_id)}
                  >
                    <div className="event-card__row">
                      <Image
                        size="4.5rem"
                        className="event-card__image"
                        url={teamPhotoOf(team, team.team_photo)}
                        reload={true}
                      />
                      <div className="event-card__body">
                        <div className="event-card__name">{team.team_name}</div>
                        <div className="event-card__date">
                          <Icon name="trophy" theme="primary" className="me-1" />
                          {team.event_name}
                        </div>
                        {team.event_date && (
                          <div className="event-card__date">
                            <Icon name="calendar" theme="primary" className="me-1" />
                            {formatDate(team.event_date)}
                          </div>
                        )}
                      </div>
                      <Icon name="angle-right" className="event-card__chevron" />
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <NotFound translationKey="noTeamsFound" loading={loading} />
          )}
        </div>
      </div>
    </Screen>
  );
};

export default Page;
